/**
 * Users API — invitation redeem (anonymous), invitation complete (auth),
 * me read (auth), profile update (auth).
 *
 * Shapes match the deployed `towninsider-api` per §7.1 of `API_CONTRACT.md`
 * v5. Drift is caught by the smoke suite running on every push/PR (see
 * `tests/integration/users.integration.test.ts`).
 *
 * Auth model: authed functions accept a `getToken` callback supplied by
 * a Clerk-aware React hook (`useAuth().getToken` from `@clerk/clerk-react`).
 * In integration tests, supplying a null-returning getter drives a 401
 * from the server — the canonical "auth middleware still in place" signal.
 *
 * Endpoints:
 *   POST /v1/users/invitation/redeem       (public, IP-rate-limited)
 *   POST /v1/users/invitation/complete     (auth)
 *   GET  /v1/users/me                      (auth)
 *   PATCH /v1/users/me/profile             (auth)
 *
 * Response envelope:
 *   - /invitation/complete and /me/profile wrap the user record in
 *     `{ user: ... }` (no partner sibling on those endpoints).
 *     `completeOnboarding` and `updateProfile` unwrap at the boundary so
 *     consumers see flat User.
 *   - /me wraps the user record AND a partner sibling in
 *     `{ user: ..., partner: ... | null }` (Batch 6a added partner;
 *     Batch 7b-1 extended partner with the persona projection). Two
 *     functions surface this:
 *       - `getMe` UNWRAPS to User — backward-compat with the
 *         pre-Batch-7b-2b contract that existing tests and direct
 *         callers depend on.
 *       - `getMeEnvelope` returns the full `{ user, partner }` envelope
 *         — used by the TanStack hooks (`useUser`, `usePartner` in
 *         `useUser.ts`) so both consumers share a single cache entry
 *         via `select` transforms and one network request.
 *
 * Schemas-vs-reality discipline: every schema in this file is backed by
 * a success-path integration test in
 * `tests/integration/users.integration.test.ts` that mints a real Clerk
 * test user via `CLERK_BACKEND_SECRET_KEY` (Batch 24c). Any shape drift
 * between this file's Zod schemas and the deployed API fails CI on the
 * PR before it can merge. Three drifts have been caught this way:
 *   - Batch 23 v1 — invitation request shape (`{invitationToken}` →
 *     `{townSlug}`), caught in browser before this safety net existed.
 *   - Batch 24b — `/me` response envelope (`{user:...}` wrap), caught
 *     in browser on first sign-in.
 *   - Batch 24c-during-day-of — PATCH `/me/profile` request shape
 *     (flat → `{profile:...}` envelope) AND response shape (`{profile}`
 *     → `{user:...}` envelope) AND semantics (partial-merge → full
 *     replacement of opaque encrypted blob). First drift caught in CI
 *     rather than the browser — exactly the use case 24c was built for.
 *   - Batch 10 (theme drift) — partner.theme field naming
 *     (snake_case `primary_colour` → camelCase `primaryHex`). Runtime
 *     was fine because `.passthrough()` preserved the unknown keys, but
 *     TypeScript typed access to `primaryHex` as `unknown`, breaking
 *     the CF Pages build. Smoke missed it because Smoke runs
 *     `tsc --noEmit` while CF Pages runs `tsc -b && vite build`
 *     (project-references mode catches things `--noEmit` doesn't).
 *     Closing the Smoke gap is tracked separately.
 */
import { z } from 'zod';
import { apiFetch } from '../api';
import { profileSchema, type Profile } from '../onboarding';

// ──── Schemas ──────────────────────────────────────────────────────

const townSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  personaName: z.string().min(1),
});

/**
 * Partner kinds — keep aligned with the API's CHECK constraint on
 * `partners.kind` (migration 0009). Adding a new value requires the API
 * CHECK and this enum to widen together; CI drift catches the integration
 * test if either side moves without the other.
 */
const partnerKindSchema = z.enum([
  'letting_agent',
  'caravan_park',
  'bnb',
  'cottage_owner',
]);

/**
 * Theme tokens for partner-branded surfaces (currently the JoinPage hero
 * accent; future welcome-message decoration and partner dashboards).
 *
 * Field naming mirrors the API's `partnerThemeSchema` exactly
 * (camelCase: `primaryHex`, `accentHex`, etc.) — same field names on
 * both sides of the wire. Pre-Batch-10 this schema declared snake_case
 * fields (`primary_colour`, `accent_colour`, `logo_ref`) which were the
 * shape of the SEED DATA but never the shape of what the API actually
 * served. `.passthrough()` preserved the real (camelCase) values at
 * runtime so JoinPage's logic kind of worked, but TypeScript typed
 * `theme.primaryHex` as `unknown` (the passthrough catchall) and the
 * CF Pages build failed on `style={{ backgroundColor: unknown }}`.
 *
 * Every field optional — a partner may supply colours without a logo,
 * or vice versa. The API enforces a stricter shape (primaryHex and
 * accentHex required) but the app stays defensively loose so a partial
 * response (e.g. a future migration that adds a new required field
 * before this client catches up) doesn't fail the whole redeem call
 * and present users with "link not valid". Missing fields fall back to
 * house style at the render site.
 *
 * `.passthrough()` retained so any future theme tokens the API adds
 * are not silently stripped at the boundary. The wider strict-projection
 * guarantee is enforced server-side by `loadPublicPartnerBySlug`; this
 * is the inner shape of a deliberately-loose payload.
 */
const partnerThemeSchema = z
  .object({
    primaryHex: z.string().optional(),
    accentHex: z.string().optional(),
    neutralHex: z.string().optional(),
    logoStoragePath: z.string().optional(),
    heroImageStoragePath: z.string().optional(),
  })
  .passthrough();

/**
 * Public partner persona projection — the narrow PUBLIC SUBSET of a
 * partner's curated persona, surfaced by both /invitation/redeem
 * (joining page) and GET /me (post-attribution) per Batch 7b-1 on the
 * API side.
 *
 * The five fields here are the persona's PUBLIC FACE — the parts a guest
 * sees: name in headers, greeting on the join screen, post-onboarding
 * welcome chip, accessibility label for the avatar.
 *
 * LLM-only fields (`toneRegister`, `voiceGuidelines`) are deliberately
 * NOT surfaced — they're consumed server-side by buildPartnerPersonaPrompt
 * (Batch 7a) to drive the chat system prompt and may be competitively-
 * sensitive partner content. The API's `loadPublicPartnerBySlug`
 * projection narrows them out at the response boundary; this client
 * schema mirrors that narrowing so we never accept them on the wire.
 *
 * Optional source fields (`displayName`, `avatarAlt`) come back as
 * `string | null` (always-present-as-nullable) — same shape as `theme`
 * on PublicPartner, so the client never has to disambiguate
 * "field absent" from "field null".
 *
 * Strict shape (no `.passthrough()`) — if the API ever adds a new field
 * to its `PublicPartnerPersona` projection without the app catching up,
 * Zod will silently strip it on this side. Drift surfaces the moment a
 * consumer expects the new field and gets undefined. Same posture as
 * `publicPartnerSchema` outer shape.
 */
const publicPartnerPersonaSchema = z.object({
  name: z.string().min(1),
  displayName: z.string().min(1).nullable(),
  greeting: z.string().min(1),
  welcomeAfterOnboarding: z.string().min(1),
  avatarAlt: z.string().min(1).nullable(),
});

/** Public partner persona shape — what app surfaces (JoinPage greeting,
 *  MePage header name, post-onboarding welcome chip) consume. */
export type PublicPartnerPersona = z.infer<typeof publicPartnerPersonaSchema>;

/**
 * Public partner projection returned by /v1/users/invitation/redeem and
 * GET /v1/users/me when a recognised `?ref=` resolves to an active
 * partner whose `town_slug` matches the joined/attributed town (server-
 * side defence-in-depth — see API repo `src/db/partners.ts` module
 * docstring for projection rationale per excluded field).
 *
 * Strictly minimal by design: slug, name, kind, theme, persona. NO id,
 * NO contact info, NO operational jsonb content beyond the narrow theme
 * and persona projections. The server enforces this at the SELECT level,
 * not just at serialisation.
 *
 * `theme` and `persona` are both `.nullable()` (not `.optional()`) so
 * the server-side null signal ("we tried and the jsonb didn't validate")
 * survives intact rather than collapsing into "not in response".
 */
const publicPartnerSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  kind: partnerKindSchema,
  theme: partnerThemeSchema.nullable(),
  persona: publicPartnerPersonaSchema.nullable(),
});

/** Public partner shape — what JoinPage and other pre-signup surfaces see. */
export type PublicPartner = z.infer<typeof publicPartnerSchema>;

/**
 * Core user record returned by every user-bearing endpoint.
 *
 * Includes the `profile` field (null for newly-created users; populated
 * after PATCH /me/profile). Used by /invitation/complete, /me, and
 * /me/profile — all three return this shape, either bare in a
 * `{ user }` envelope (`/complete`, `/me/profile`) or alongside a
 * partner sibling in a `{ user, partner }` envelope (`/me`).
 *
 * `partnerRef` is the partner slug the user signed up under (captured
 * from `?ref=` on their /join URL — see JoinPage and storage.ts).
 * Always present on the wire (the API selects it explicitly in
 * `findActiveUserWithProfile`); `null` for users who arrived without a
 * matching active partner. Schemarised as `.nullable()` (not
 * `.nullish()`) so that drift-catching fires loudly if the API ever
 * stops returning the field — silent absence would mask an attribution
 * regression that wouldn't surface until partner dashboards showed
 * empty results.
 *
 * Note: the API also returns `emailStatus` (e.g. "active"). Zod's
 * default behaviour strips unknown keys silently, so we omit it from
 * the schema until a consumer needs it. When that happens, add it
 * here as `emailStatus: z.enum([...])` and back it with an integration
 * assertion on the enum values.
 */
const userSchema = z.object({
  id: z.string().min(1),
  townSlug: z.string().min(1),
  joinedAt: z.string().min(1), // ISO 8601 timestamp
  email: z.string().email(),
  profile: profileSchema.nullable(),
  partnerRef: z.string().nullable(),
});

/** Unwrapped user shape consumers see. */
export type User = z.infer<typeof userSchema>;

/**
 * `{ user }` envelope used by /invitation/complete and /me/profile.
 *
 * These two endpoints return ONLY the user record (no partner sibling).
 * `completeOnboarding` and `updateProfile` unwrap at the boundary so
 * consumers see flat User. See `meResponseSchema` below for GET /me
 * which carries a partner sibling.
 */
const userEnvelopeSchema = z.object({
  user: userSchema,
});

/**
 * `{ user, partner }` envelope used by GET /me (Batch 6a added the
 * partner sibling; Batch 7b-1 extended partner with the persona
 * projection).
 *
 * Two functions surface this envelope:
 *   - `getMe` UNWRAPS to User for backward-compat with pre-Batch-7b-2b
 *     direct callers (integration tests, anything that needed just
 *     the user record).
 *   - `getMeEnvelope` returns the full envelope for hooks
 *     (`useUser` + `usePartner` in `useUser.ts`) that share a TanStack
 *     Query cache entry via `select` transforms.
 */
const meResponseSchema = z.object({
  user: userSchema,
  partner: publicPartnerSchema.nullable(),
});

/**
 * GET /v1/users/me's full envelope: user record AND the partner
 * sibling (the public projection the user was attributed to at
 * sign-up, including the partner-curated persona when populated).
 *
 * Used by `getMeEnvelope` and threaded through the TanStack Query
 * cache so that `useUser` and `usePartner` can both read from a
 * single shared cache entry via `select` transforms. See useUser.ts
 * for the select pattern.
 */
export type MeEnvelope = z.infer<typeof meResponseSchema>;

/**
 * Response from POST /v1/users/invitation/redeem.
 *
 * `partner` is ALWAYS present on the wire — `null` for bare URLs,
 * unrecognised refs, archived partners, or cross-town crafted URLs
 * (the API's town-match defence-in-depth). `.nullable()` (not
 * `.nullish()`) so silent absence fires the drift catcher rather than
 * masking an attribution regression.
 */
export const redeemInvitationResponseSchema = z.object({
  town: townSchema,
  partner: publicPartnerSchema.nullable(),
});

export type RedeemInvitationResponse = z.infer<
  typeof redeemInvitationResponseSchema
>;

// Endpoint-named type aliases. All three endpoints return the same
// unwrapped User shape; the aliases exist to document intent at call
// sites. /me's full envelope (with partner sibling) is `MeEnvelope`
// above — separate type so the unwrap-vs-envelope distinction is
// explicit at call sites.
export type CompleteOnboardingResponse = User;
export type GetMeResponse = User;
export type UpdateProfileResponse = User;

// ──── Inputs ───────────────────────────────────────────────────────

export interface RedeemInvitationInput {
  readonly townSlug: string;
  /**
   * Partner slug captured from a `?ref=...` query parameter on the
   * /join/:town URL. Optional — bare-URL sign-ups carry no ref.
   *
   * Length-bound the value at the call site (REF_MAX_LENGTH=64 in
   * JoinPage) to match the API's `RedeemSchema.ref` bound exactly.
   *
   * An unknown, inactive, or town-mismatched ref resolves to
   * `partner: null` server-side — the API does NOT 400 on a bad ref, so
   * a stale or mistyped QR cannot break onboarding. Attribution is
   * best-effort; sign-up is not.
   */
  readonly ref?: string;
  readonly signal?: AbortSignal;
}

export interface CompleteOnboardingInput {
  readonly townSlug: string;
  /**
   * Partner slug captured from a `?ref=...` query parameter on the
   * /join/:town URL (see `JoinPage` and `pendingRef` in storage.ts).
   * Optional — bare-URL or picker-driven sign-ups carry no ref.
   *
   * The API matches this against `partners.slug` and writes
   * `users.partner_ref` on the create path only (first-touch attribution).
   * An unknown or inactive ref resolves to NULL server-side; the API
   * does not 400 on a bad ref, so a stale or mistyped QR cannot break
   * onboarding.
   */
  readonly ref?: string;
  readonly getToken: () => Promise<string | null>;
  readonly signal?: AbortSignal;
}

export interface GetMeInput {
  readonly getToken: () => Promise<string | null>;
  readonly signal?: AbortSignal;
}

export interface UpdateProfileInput {
  /**
   * The COMPLETE profile to write. Not a partial. See `updateProfile`
   * docstring for full-replacement semantics and the spread-then-override
   * pattern callers should use to "update one field".
   */
  readonly profile: Profile;
  readonly getToken: () => Promise<string | null>;
  readonly signal?: AbortSignal;
}

// ──── Calls ────────────────────────────────────────────────────────

/**
 * Validate a town slug against the API, and (if a recognised `ref` is
 * supplied) resolve the partner attribution to its public projection.
 * Anonymous endpoint — no auth required, rate-limited per IP by the
 * server.
 *
 * `ref` is sent via conditional spread — we never transmit `ref:
 * undefined` (the API's `z.string()` schema would reject it; we mean
 * "no ref", not "explicit undefined").
 *
 * Throws `ApiError` on:
 *   - 404 'not_found'    — town not recognised
 *   - 5xx 'server_error' — server error
 *   - 0   'network_error' — connectivity failure
 *
 * Returns `{ town, partner }`. `partner` is `null` whenever no
 * attribution applies (bare URL, unrecognised slug, archived partner,
 * town-mismatched crafted URL).
 */
export async function redeemInvitation(
  input: RedeemInvitationInput,
): Promise<RedeemInvitationResponse> {
  return apiFetch('/v1/users/invitation/redeem', {
    method: 'POST',
    body: {
      townSlug: input.townSlug,
      ...(input.ref !== undefined ? { ref: input.ref } : {}),
    },
    schema: redeemInvitationResponseSchema,
    ...(input.signal !== undefined ? { signal: input.signal } : {}),
  });
}

/**
 * Reconcile a Clerk-authed session into a TownInsider user row.
 * Idempotent: subsequent calls for the same Clerk user return the
 * existing row.
 *
 * Called once after Clerk sign-up completes (magic link clicked,
 * session active) to create or fetch the user row keyed by Clerk
 * user ID with the town slug from the in-flight onboarding context.
 *
 * If `ref` is supplied, the API attempts best-effort partner attribution
 * — see `CompleteOnboardingInput.ref` doc. The body is constructed with
 * a conditional spread so we never send `ref: undefined` (would be
 * rejected by the API's `z.string()` schema; we mean "no ref", not
 * "explicit undefined").
 *
 * Returns the user record (profile is null for fresh sign-ups). The
 * /complete endpoint does NOT return a partner sibling on the
 * response — partner data lands on the next GET /me, where the
 * combined envelope hits the cache for useUser + usePartner.
 */
export async function completeOnboarding(
  input: CompleteOnboardingInput,
): Promise<CompleteOnboardingResponse> {
  const envelope = await apiFetch('/v1/users/invitation/complete', {
    method: 'POST',
    body: {
      townSlug: input.townSlug,
      ...(input.ref !== undefined ? { ref: input.ref } : {}),
    },
    schema: userEnvelopeSchema,
    getToken: input.getToken,
    ...(input.signal !== undefined ? { signal: input.signal } : {}),
  });
  return envelope.user;
}

/**
 * Fetch the FULL /me envelope: user record AND partner attribution.
 * Auth required.
 *
 * Returns the `{ user, partner }` envelope without unwrapping — the
 * partner sibling carries the public projection the user was attributed
 * to at sign-up (slug, name, kind, theme, persona) or `null` when no
 * attribution applies.
 *
 * Used by the TanStack hooks (`useUser` and `usePartner` in
 * `useUser.ts`) so that the combined envelope hits a single cache
 * entry; the hooks then derive their respective consumer views via
 * `select` transforms. Calling both hooks in the same render fires
 * ONE network request (TanStack dedupes by query key).
 *
 * Direct callers who want just the user record should use `getMe`
 * (below) — that's the backward-compat unwrap path the existing
 * integration tests depend on.
 */
export async function getMeEnvelope(
  input: GetMeInput,
): Promise<MeEnvelope> {
  return apiFetch('/v1/users/me', {
    method: 'GET',
    schema: meResponseSchema,
    getToken: input.getToken,
    ...(input.signal !== undefined ? { signal: input.signal } : {}),
  });
}

/**
 * Fetch the current user's identity + profile, unwrapped. Auth required.
 *
 * Returns just the `User` record — the partner sibling on /me's wire
 * envelope is dropped at this boundary for backward-compat with the
 * pre-Batch-7b-2b contract that direct callers (integration tests in
 * `tests/integration/users.integration.test.ts`, ad-hoc one-off calls)
 * depend on.
 *
 * Consumers who need partner data should use `useUser` + `usePartner`
 * from `useUser.ts` (which share a TanStack cache populated by
 * `getMeEnvelope` — one network request, two derived views).
 *
 * `profile` is null for users who haven't completed profile capture
 * yet (just-redeemed accounts pre-onboarding-step-1). After profile
 * capture, the `profile` field is the full structured shape defined
 * in `src/lib/onboarding.ts`.
 */
export async function getMe(input: GetMeInput): Promise<GetMeResponse> {
  const envelope = await getMeEnvelope(input);
  return envelope.user;
}

/**
 * Replace the current user's profile. Auth required.
 *
 * **Full replacement, not merge.** The API treats the profile as an
 * opaque encrypted blob — whatever shape is sent fully replaces what
 * was there. To update a single field, callers must spread the current
 * profile and override:
 *
 *   await updateProfile({
 *     profile: { ...currentProfile, allergies: ['nuts'] },
 *     getToken,
 *   });
 *
 * Returns the user record with the just-written profile attached.
 * Response envelope is `{ user }` — partner is NOT included on the
 * PATCH response. `useUpdateProfile`'s onSuccess merges the returned
 * user into the cached combined `{ user, partner }` shape while
 * preserving the partner sibling, so usePartner consumers don't see
 * their data evicted on profile saves.
 */
export async function updateProfile(
  input: UpdateProfileInput,
): Promise<UpdateProfileResponse> {
  const envelope = await apiFetch('/v1/users/me/profile', {
    method: 'PATCH',
    body: { profile: input.profile },
    schema: userEnvelopeSchema,
    getToken: input.getToken,
    ...(input.signal !== undefined ? { signal: input.signal } : {}),
  });
  return envelope.user;
}
